import { TFunction } from "i18next";
import { isoToShortTime, isoToTimeString, ordinal } from "@/utils/datetime";
import { joinWithAnd } from "@/utils/strings";
import {
  DayOfQuarter,
  DayOfYear,
  Frequency,
  ScheduleData,
  ScheduleSummaryStyle
} from "@/components/global/Chat/components/ScheduleModal/types";

const NAMESPACE_BY_STYLE: Record<ScheduleSummaryStyle, string> = {
  delivery: "chat.scheduleModal.descriptions",
  scheduledFor: "chat.scheduleModal.descriptionsScheduledFor",
  recurring: "chat.scheduleModal.recurringReports.summary"
};

const DAYS_KEY = "chat.scheduleModal.days";
const DAY_POSITION_KEY = "chat.scheduleModal.dayPosition";

type SummaryInput = Pick<
  ScheduleData,
  "frequencyUnit" | "dayOfPeriod" | "frequencyCount" | "deliveryTime"
>;

interface FrequencyShape {
  baseKey: string;
  vars?: Record<string, string>;
}

const FREQUENCY_RESOLVERS: Record<
  Frequency,
  (t: TFunction, input: SummaryInput) => FrequencyShape
> = {
  [Frequency.DAY]: () => ({ baseKey: "daily" }),

  [Frequency.WEEK]: (t, { dayOfPeriod }) => {
    const selected = Array.isArray(dayOfPeriod)
      ? dayOfPeriod
      : dayOfPeriod
        ? [dayOfPeriod]
        : [];
    const joined = joinWithAnd(selected.map((d) => t(`${DAYS_KEY}.${d}`)));
    return { baseKey: "weekly", vars: { day: joined, days: joined } };
  },

  [Frequency.MONTH]: (_t, { dayOfPeriod }) => {
    const dayNum = typeof dayOfPeriod === "string" ? Number(dayOfPeriod) : 1;
    return { baseKey: "monthly", vars: { day: ordinal(dayNum) } };
  },

  [Frequency.QUARTER]: (t, { dayOfPeriod }) => {
    const isLast = dayOfPeriod === DayOfQuarter.LAST;
    const position = t(`${DAY_POSITION_KEY}.${isLast ? "last" : "first"}`);
    return { baseKey: "quarterly", vars: { position } };
  },

  [Frequency.YEAR]: (t, { dayOfPeriod }) => {
    const value =
      typeof dayOfPeriod === "string" ? dayOfPeriod : DayOfYear.FIRST;
    const isFiscal =
      value === DayOfYear.FIRST_FISCAL || value === DayOfYear.LAST_FISCAL;
    const isLast = value === DayOfYear.LAST || value === DayOfYear.LAST_FISCAL;
    const position = t(`${DAY_POSITION_KEY}.${isLast ? "last" : "first"}`);
    return {
      baseKey: isFiscal ? "yearlyFiscal" : "yearly",
      vars: { position }
    };
  }
};

export const buildScheduleSummary = (
  t: TFunction,
  scheduleConfig: SummaryInput,
  style: ScheduleSummaryStyle = "delivery"
): string => {
  const resolver = FREQUENCY_RESOLVERS[scheduleConfig.frequencyUnit];
  if (!resolver) return "";

  const { baseKey, vars } = resolver(t, scheduleConfig);
  const isMany = scheduleConfig.frequencyCount > 1;
  const namespace = NAMESPACE_BY_STYLE[style];

  return t(`${namespace}.${baseKey}${isMany ? "Many" : ""}`, {
    count: scheduleConfig.frequencyCount,
    time:
      style === "recurring"
        ? isoToShortTime(scheduleConfig.deliveryTime)
        : undefined,
    ...vars
  });
};

/** "Scheduled for … at …" — the one-line description of a report's cadence. */
export const buildScheduledForText = (
  t: TFunction,
  scheduleConfig: SummaryInput
): string =>
  t("chat.scheduleModal.scheduledTooltip", {
    description: buildScheduleSummary(t, scheduleConfig, "scheduledFor"),
    time: isoToTimeString(scheduleConfig.deliveryTime)
  });
